import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "@/router";
import type { Path, Params } from "@/router";
import { useQuery } from "@apollo/client";
import { LinkProps } from "@generouted/react-router/client";
import { EventRegistration, Session, Address } from "./types";
import { EVENT_REGISTRATION_QUERY } from "./queries";
import usePageTitle from "@/hooks/usePageTitle";
import { useReturnTo } from "@/hooks/useSearchParams";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import PageHeading from "@/components/PageHeading";
import Table, { TableHeading, buildDocumentLinkColumn } from "@/components/Table";
import IconButton from "@/components/IconButton";
import Menu from "@/components/Menu";
import DetailsCard from "@/components/DetailsCard";
import ListItem from "@/components/ListItem";
import Button from "@/components/Button";
import Icon, { SlotIconName } from "@/components/Icon";
import { ButtonVariant } from "@/components/Button/Button";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import Banner from "@/components/Banner";
import SectionHeading from "@/components/SectionHeading";
import CancelModal from "../../_components/CancelModal";
import CancelRegistrationInfoModal from "../../_components/CancelRegistrationInfoModal";
import TransferRegistrationModal from "../../(tabbed)/registrations/_components/TransferRegistrationModal";
import { renderStatus } from "@/pages/billing/_utils";
import { OrderStage, OrderPaymentStatus } from "@/pages/billing/types";
import { getHours, getTime, toFormat, toDateTimeFormat, getAge } from "@/lib/dateHelpers";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import EmptyState from "@/components/EmptyState";
import Stepper from "@/components/Stepper";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import useIsSectionScopedForEvents from "@/pages/events/useIsSectionScopedForEvents";
import { getEventTypeName, isCancelRegistrationPerformable } from "../../utils";
import useCanCancelRegistration from "../../useCanCancelRegistration";
import { useEventDetailQueryDocument } from "../../useEventDetailQuery";
import NotesTable, { useNoteDeepLink, usePinnedNotes } from "@/features/global/components/NotesTable";
import { getNoteParentLink } from "@/features/global/components/NotesTable/noteHelpers";
import { NoteNotableValues } from "@/types";
import type { CRMNote } from "@/types";

function formattedAddress(address: Address) {
  return `${address.address1}${address.address2 ? `, ${address.address2}` : ""}`;
}

const booleanToYesNo = (bool: boolean) => (bool ? "Yes" : "No");

function transformSessionData(sessions: Session[], timezone?: string) {
  return sessions.map((session) => ({
    ...session,
    id: session.id,
    startsAt: toFormat(session.startsAt),
    endsAt: toFormat(session.endsAt),
    duration: getHours(session.duration),
    startTime: getTime(session.startsAt, undefined, timezone),
    endTime: getTime(session.endsAt, undefined, timezone)
  }));
}

function CustomFieldsDetail({ eventRegistration }: { eventRegistration: EventRegistration }) {
  if (!eventRegistration.customFields) return null;
  const { adaAccomodations, additionalGuests, dateOfBirth = "", gender } = eventRegistration.customFields;

  const formattedDateOfBirth = (dateOfBirth: string) =>
    toFormat(dateOfBirth) ? `${toFormat(dateOfBirth)} (${getAge(dateOfBirth)?.years} yrs)` : null;
  const dobHeadline = formattedDateOfBirth(dateOfBirth);

  const formattedAdditionalGuests = (additionalGuests ?? 0).toString();

  return (
    <div data-testid="pat-registration-detail">
      {dobHeadline && (
        <div className="mb-4">
          <ListItem headline={dobHeadline} iconName="cake" iconBackground={true} overline="Date of Birth" />
        </div>
      )}
      {gender && (
        <div className="mb-4">
          <ListItem headline={gender} iconName="person" iconBackground={true} overline="Gender" />
        </div>
      )}
      {additionalGuests && (
        <div className="mb-4">
          <ListItem
            headline={formattedAdditionalGuests}
            iconName="group_add"
            iconBackground={true}
            overline="Additional Guests"
          />
        </div>
      )}
      {typeof adaAccomodations === "boolean" && (
        <div className="mb-4">
          <ListItem
            headline={booleanToYesNo(adaAccomodations)}
            iconName="accessible_forward"
            iconBackground={true}
            overline="ADA"
          />
        </div>
      )}
    </div>
  );
}

function RegistrationDetail({
  eventRegistration,
  navigate,
  eventSlug,
  basePath
}: {
  eventRegistration: EventRegistration;
  navigate: (path: any) => void;
  eventSlug: string;
  basePath: string;
}) {
  const hasCustomFields = eventRegistration.event.customFields.length > 0;

  return (
    <div>
      {eventRegistration.canceledAt && (
        <div className="mt-8">
          <Banner
            variant="warn"
            inline={true}
            description={`Registration Canceled on ${toDateTimeFormat(eventRegistration.canceledAt)}`}
            closable={false}
          />
        </div>
      )}
      <SectionHeading heading="Registration Details" />
      <div className="mb-4">
        <ListItem
          headline={
            <div className="flex items-center gap-2">
              <span>{eventRegistration.event.title}</span>
              <Icon name="navigate_next" className="text-sys-brand-on-surface-variant" />
            </div>
          }
          iconName="event"
          iconBackground={true}
          overline="Event"
          action={() => navigate(`${basePath}/${eventSlug}` as any)}
          renderActions={() => <></>}
        />
      </div>
      {hasCustomFields && <CustomFieldsDetail eventRegistration={eventRegistration} />}
    </div>
  );
}

export default function RegistrationDetailsPage() {
  const { eventSlug = "", registrationId = "" } = useParams();
  const basePath = useEventsBasePath();
  const navigate = useNavigate();
  const returnTo = useReturnTo();
  const [backLink] = useState({
    to: (returnTo || `${basePath}/${eventSlug}/registrations`) as LinkProps<Path, Params>["to"]
  } as LinkProps<Path, Params>);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actions, setActions] = useState<ReactNode>(null);
  const [openCancelRegistrationModal, setOpenCancelRegistrationModal] = useState(false);
  const [openCancelRegistrationInfoModal, setOpenCancelRegistrationInfoModal] = useState(false);
  const [openTransferRegistrationModal, setOpenTransferRegistrationModal] = useState(false);

  const eventDetailQuery = useEventDetailQueryDocument();

  const { data: eventData, loading: eventLoading } = useQuery(eventDetailQuery, {
    variables: { slug: eventSlug }
  });

  const event = eventData?.event;

  const {
    data: registrationData,
    loading: registrationLoading,
    refetch
  } = useQuery<{ eventRegistration: EventRegistration }>(EVENT_REGISTRATION_QUERY, {
    variables: {
      id: registrationId
    }
  });

  const eventRegistration = registrationData?.eventRegistration;
  const isSeminar = getEventTypeName(event)?.toLowerCase().includes("seminar");

  const isSectionScoped = useIsSectionScopedForEvents();
  const canAccessActivityManagement =
    useActivityTypePermissions(event?.activityType ?? { title: getEventTypeName(event) }) &&
    (!isSectionScoped || !!event?.section?.permissions?.manageEvents);
  const hasCancelPermission = useCanCancelRegistration(event, {
    canAccessActivityManagement
  });
  const canSeeCancelRegistration = !eventRegistration?.canceledAt && hasCancelPermission;
  const canPerformCancelRegistration = isCancelRegistrationPerformable(event);
  const canTransferRegistration = !eventRegistration?.canceledAt && canAccessActivityManagement;

  const { firstName, lastName, pgaId } = eventRegistration?.person || {};

  const {
    highlightNoteId: deepLinkNoteId,
    openNoteId,
    getCopyLink,
    setOpenNoteInUrl
  } = useNoteDeepLink({
    dataReady: !registrationLoading && !!eventRegistration
  });
  const { highlightNoteId: pinnedHighlightNoteId } = usePinnedNotes({
    notes: eventRegistration?.notes || [],
    entityId: registrationId,
    sessionStorageKey: "visitedEventRegistrationDetails"
  });
  const highlightNoteId = pinnedHighlightNoteId ?? deepLinkNoteId;

  const getParentLink = useCallback(
    (note: CRMNote) =>
      getNoteParentLink(note, {
        pgaId: pgaId || "",
        eventSlug: eventSlug || eventRegistration?.event.slug
      }),
    [eventSlug, eventRegistration?.event.slug, pgaId]
  );

  // Set page title with event and person context
  const personName = firstName && lastName ? `${firstName} ${lastName}` : "";
  const eventTitle = event?.title || "Event";
  usePageTitle(
    `${basePath}/:eventSlug/registrations/:registrationId`,
    personName ? `${eventTitle} | ${personName}` : eventTitle
  );

  const personData = eventRegistration?.person;
  const address = personData?.addresses?.find((address) => address.addressType === "home");
  const phone = personData?.phoneNumbers.find((phone) => phone.phoneType === "mobile");
  const hasContactInfo = personData?.email || address || phone?.phoneNumber;
  const order = eventRegistration?.order;
  const isFinalized = event?.finalizedAt;

  const sessionData = useMemo(() => {
    if (!eventRegistration?.sessions || !event?.timezone) {
      return [];
    }
    return transformSessionData(eventRegistration.sessions, event.timezone);
  }, [eventRegistration?.sessions, event?.timezone]);

  const documentData = useMemo(() => {
    const document = eventRegistration?.submission?.document;
    if (!document) {
      return [];
    }
    return [
      {
        id: `doc-${document.name}`,
        documentName: document.name,
        uploadDate: toDateTimeFormat(document.createdAt),
        url: document.url
      }
    ];
  }, [eventRegistration?.submission?.document]);

  const registrationHistorySteps = useMemo(() => {
    const canceledAt = eventRegistration?.canceledAt;
    const registeredAt = eventRegistration?.registeredAt;

    if (!canceledAt && !registeredAt) return [];

    const steps = [];

    // Add cancellation date step first if canceled (newest)
    if (canceledAt) {
      steps.push({
        headline: "Canceled On",
        supportingText: toDateTimeFormat(canceledAt) || ""
      });
    }

    // Add registration date step (oldest)
    if (registeredAt) {
      steps.push({
        headline: "Registered On",
        supportingText: toDateTimeFormat(registeredAt) || ""
      });
    }

    return steps;
  }, [eventRegistration?.canceledAt, eventRegistration?.registeredAt]);

  const registrationButtons = useMemo(() => {
    if (registrationLoading || !eventRegistration) {
      return [];
    }
    return [
      ...(!isFinalized
        ? [
            {
              key: "cancel-registration",
              label: "Cancel Registration",
              icon: "delete",
              variant: "text" as ButtonVariant,
              onClick: () => {
                if (canPerformCancelRegistration) {
                  setOpenCancelRegistrationModal(true);
                } else {
                  setOpenCancelRegistrationInfoModal(true);
                }
              },
              show: canSeeCancelRegistration
            }
          ]
        : []),
      ...(isSeminar && !isFinalized
        ? [
            {
              key: "transfer-registration",
              label: "Transfer Registration",
              icon: "swap_horiz",
              variant: "text" as ButtonVariant,
              onClick: () => {
                setOpenTransferRegistrationModal(true);
              },
              show: canTransferRegistration
            }
          ]
        : []),
      {
        key: "view-full-profile",
        label: "View Full Profile",
        icon: "open_in_new",
        variant: "outlined" as ButtonVariant,
        onClick: () => {
          window.open(`/people/${pgaId}/events`, "_blank");
        },
        show: !!pgaId
      }
    ];
  }, [
    registrationLoading,
    eventRegistration,
    canSeeCancelRegistration,
    canPerformCancelRegistration,
    canTransferRegistration,
    pgaId,
    isSeminar,
    isFinalized
  ]);

  useEffect(() => {
    setActions(
      <>
        {registrationButtons
          .filter((button) => button.show)
          .map((button) => (
            <Button
              key={button.key}
              variant={button.variant}
              icon={button.icon as SlotIconName}
              onClick={button.onClick}
              testId={button.key}
            >
              {button.label}
            </Button>
          ))}
      </>
    );
  }, [registrationButtons, setActions]);

  return (
    <FocusedPageLayout
      backLink={backLink}
      actions={React.isValidElement(actions) ? actions.props.children : []}
      stickyActions={true}
      renderBanner={() => errorMessage && <Banner closable={true} description={errorMessage} variant="error" />}
    >
      <div data-testid="registration-details-page">
        <PageHeading title="Event Registration Details" />
        {pgaId && <PersonObjectHeading pgaId={pgaId} hideActions={true} disableScrollAdjustment={true} />}
        {eventRegistration && (
          <RegistrationDetail
            eventRegistration={eventRegistration}
            navigate={navigate}
            eventSlug={eventSlug!}
            basePath={basePath}
          />
        )}

        {!registrationLoading && hasContactInfo && (
          <div>
            <SectionHeading heading="Registrant Contact Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              {address && (
                <div>
                  <DetailsCard
                    heading="Address"
                    options={[
                      {
                        iconName: "home",
                        overline: "Home",
                        headline: formattedAddress(address),
                        supportingText: `${address.city}, ${address.state} ${address.postalCode}`
                      }
                    ]}
                  />
                </div>
              )}
              {personData?.email && (
                <div>
                  <DetailsCard
                    heading="Email Address"
                    options={[
                      {
                        headline: personData?.email,
                        iconName: "mail"
                      }
                    ]}
                  />
                </div>
              )}
              {phone?.phoneNumber && (
                <div>
                  <DetailsCard
                    heading="Phone"
                    options={[
                      {
                        overline: "Mobile/Cell",
                        headline: phone?.phoneNumber,
                        iconName: "call"
                      }
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {order && (
          <>
            <SectionHeading heading="Order" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              <DetailsCard
                className="py-4 pl-4 pr-0"
                options={[
                  {
                    iconName: "receipt_long",
                    overline: `Order`,
                    headline: `${firstName} ${lastName}`,
                    supportingText: `${toDateTimeFormat(order.updatedAt)}`,
                    action: () =>
                      navigate(`/billing/orders/:orderId`, {
                        params: { orderId: order.id }
                      }),
                    renderActions: () => (
                      <div className="flex items-center -mt-3">
                        <span className="text-label-small text-sys-brand-on-surface-variant">
                          {centsToDollarsFormatted(order.balance)}
                        </span>
                        <Menu
                          AnchorComponent={
                            <IconButton
                              name="more_vert"
                              className="text-sys-brand-on-surface"
                              iconClassName="font-extrabold"
                            />
                          }
                          menuItems={[
                            {
                              label: "View Order",
                              onClick: () =>
                                navigate(`/billing/orders/:orderId`, {
                                  params: { orderId: order.id }
                                })
                            }
                          ]}
                        />
                      </div>
                    ),
                    status: () =>
                      renderStatus(
                        order.status as unknown as OrderStage,
                        order.paymentStatus as unknown as OrderPaymentStatus
                      )
                  }
                ]}
              />
            </div>
          </>
        )}

        <TableHeading heading="Sessions" body="View all sessions associated with this registration" />
        <Table
          data={sessionData || []}
          loading={registrationLoading || eventLoading}
          columns={[
            {
              accessorKey: "startsAt",
              header: "Start Date",
              enableSorting: false,
              meta: {
                className: getStickyColumnClasses(!!sessionData?.length)
              }
            },
            {
              accessorKey: "endsAt",
              header: "End Date",
              enableSorting: false
            },
            {
              accessorKey: "startTime",
              header: "Start Time",
              enableSorting: false
            },
            {
              accessorKey: "endTime",
              header: "End Time",
              enableSorting: false
            },
            {
              accessorKey: "title",
              header: "Day of the Week",
              enableSorting: false
            },
            {
              accessorKey: "duration",
              header: "Duration",
              enableSorting: false
            }
          ]}
        />

        {eventRegistration && (
          <NotesTable
            data={eventRegistration.notes || []}
            hideControls
            tableHeading="Notes"
            tableBody="View and manage notes for this event registration"
            highlightNoteId={highlightNoteId}
            openNoteId={openNoteId}
            onOpenNoteChange={setOpenNoteInUrl}
            getCopyLink={getCopyLink}
            getParentLink={getParentLink}
            noteEditing={{
              enabled: canAccessActivityManagement,
              notableId: eventRegistration.id,
              notableType: NoteNotableValues.EVENT_REGISTRATION,
              refetchQueries: [EVENT_REGISTRATION_QUERY],
              pgaId
            }}
          />
        )}

        <TableHeading heading="Documents" body="View all documents uploaded for this registration" />
        <Table
          data={documentData}
          loading={registrationLoading}
          columns={[
            buildDocumentLinkColumn<(typeof documentData)[number]>({
              accessorKey: "documentName",
              header: "Document Name",
              getDocumentName: (row) => row.documentName,
              getDocumentUrl: (row) => row.url
            }),
            {
              header: "Upload Date",
              accessorKey: "uploadDate",
              meta: { sortType: "date" }
            }
          ]}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Associated Documents"
              caption="There are no documents associated with this registration"
              iconName="description"
            />
          )}
          data-testid="registration-documents-table"
        />

        {eventRegistration && registrationHistorySteps.length > 0 && (
          <div>
            <SectionHeading heading="Registration History" />
            <Stepper
              variant="outlined"
              stepsList={registrationHistorySteps}
              onStepClick={() => {}} // No action needed for history steps
            />
          </div>
        )}

        {openCancelRegistrationModal && eventRegistration && (
          <CancelModal
            event={eventRegistration.event}
            eventRegistrationId={eventRegistration.id}
            open={openCancelRegistrationModal}
            setOpen={setOpenCancelRegistrationModal}
            onError={(message: string) => setErrorMessage(message)}
            order={eventRegistration.order}
            refetch={refetch}
          />
        )}

        {openCancelRegistrationInfoModal && (
          <CancelRegistrationInfoModal
            open={openCancelRegistrationInfoModal}
            onClosed={() => setOpenCancelRegistrationInfoModal(false)}
          />
        )}

        {openTransferRegistrationModal && eventRegistration && pgaId && (
          <TransferRegistrationModal
            registrationId={eventRegistration.id}
            registrantName={`${firstName} ${lastName}`}
            registrantPgaId={pgaId}
            currentEventId={event?.id || ""}
            onClosed={() => setOpenTransferRegistrationModal(false)}
            refetch={refetch}
          />
        )}
      </div>
    </FocusedPageLayout>
  );
}
